/* eslint-disable react/prop-types */
import { Box, Divider, Grid } from "@mui/material";
import { Formik } from "formik";
import React, { useState } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Button from "../../../components/common/Button";
import OrganizationStructure from "../../../components/common/OrganizationStructure/OrganizationStructure";
import { NEW_FRIEND_FIELDS } from "../../../contants/formFields";
import MembersApi from "../../../services/api/member";
import { handleFriendsToMemberValidation } from "../../../utils";
import { debounce } from "../../../utils/helper";
import NewFriendForm from "../NewMemberEntry";
import { CommonStyles } from "../../../components/common/Button/commonButtonStyle";
// import { ToastAlertContext } from "../../../config/providers/ToastAlert";
import { OS_KEYS } from "../../../contants";
import Toast from "../../../components/common/Toast";
import Modal from "../../../components/common/Modal/Modal";
import Typography from "../../../components/common/new/Typography/Typography";

function Alert({ open, onClose, onSubmit }) {
  return (
    <Box>
      <Modal open={open} onClose={onClose}>
        <Typography
          style={{ fontSize: "20px", fontWeight: "500", paddingBottom: "20px" }}
        >
          Member with similar details exists!
          <br />
          Do you want to continue?
        </Typography>
        <Box style={{ paddingTop: "50px", justifyContent: "space-between" }}>
          <Button
            onClick={onSubmit}
            label="Yes"
            style={{ marginRight: "10px" }}
          />
          <Button onClick={onClose} label="No" variant="outlined" />
        </Box>
      </Modal>
    </Box>
  );
}

function MemberCreate() {
  // const toast = useContext(ToastAlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(null);
  const navigate = useNavigate();
  const selectedFriend = useSelector(
    (state) => state.view_members.selectedFriendFromSearch
  );
  const { id } = useParams();
  const [openAlert, setOpenAlert] = useState(false);

  const onBack = () => navigate(-1);

  const toggleAlert = () => {
    setOpenAlert(false);
  };

  const postData = async (values) => {
    try {
      setIsLoading(true);
      const response = await MembersApi.changeToMember({
        data: { ...values, ignoreDuplicate: false, siConsent: false },
        friendId: id,
      });
      setTimeout(() => {
        onBack();
      }, 2000);
      if (response) {
        setShowToast({
          message: "Form submitted successfully",
          severity: "success",
        });
      }
      setIsLoading(false);
    } catch (err) {
      if (err.status === 409) {
        setOpenAlert(true);
      } else {
        setShowToast({ message: err.message, severity: "error" });
      }
      setIsLoading(false);
      Sentry.captureException(err);
    }
  };

  const handleAlertYes = async (values) => {
    setOpenAlert(false);
    try {
      setIsLoading(true);
      const response = await MembersApi.changeToMember({
        data: { ...values, ignoreDuplicate: false, siConsent: true },
        friendId: id,
      });
      setTimeout(() => {
        onBack();
      }, 2000);
      if (response) {
        setShowToast({
          message: "Form submitted successfully",
          severity: "success",
        });
      }
      setIsLoading(false);
    } catch (err) {
      setShowToast({ message: err.message, severity: "error" });
      setIsLoading(false);
      Sentry.captureException(err);
    }
  };

  return (
    <>
      <Grid container>
        <Formik
          initialValues={{
            nationalityId: selectedFriend?.nationalityId || "",
            ...selectedFriend,
            [NEW_FRIEND_FIELDS.NAME]: selectedFriend.fullName,
            [NEW_FRIEND_FIELDS.ADDRESS_LINE_1]: selectedFriend.addressLine1,
            [NEW_FRIEND_FIELDS.PINCODE]: selectedFriend.pincode,
            [NEW_FRIEND_FIELDS.STATE]: parseInt(selectedFriend.state, 10),
            [NEW_FRIEND_FIELDS.CITY]: parseInt(selectedFriend.city, 10),
            [NEW_FRIEND_FIELDS.DATE_OF_JOINING]: selectedFriend.createdAt,
            [NEW_FRIEND_FIELDS.CONTACT_NUMBER]: selectedFriend.mobileNumber,
            [NEW_FRIEND_FIELDS.EMAIL_ADDRESS]: selectedFriend.emailId,
            [NEW_FRIEND_FIELDS.DIVISION]: selectedFriend.divisionId,
            [NEW_FRIEND_FIELDS.SUB_DIVISION]: selectedFriend.subdivisionId,
            [NEW_FRIEND_FIELDS.INTRODUCED_BY_NAME]:
              selectedFriend.introducerName,
            [NEW_FRIEND_FIELDS.INTRODUCED_BY_MOBILE_NUMBER]:
              selectedFriend.introducerMobileNumber,
            [NEW_FRIEND_FIELDS.INTRODUCED_BY_EMAIL]:
              selectedFriend.introducerEmail,
          }}
          onSubmit={debounce((values) => {
            const result = handleFriendsToMemberValidation(
              NEW_FRIEND_FIELDS,
              values
            );
            if (result && Object.keys(result).length) {
              setShowToast(result);
              return;
            }
            postData(values);
          })}
        >
          {({ values, errors, handleChange, handleSubmit, setFieldValue }) => (
            <>
              <Box display="flex" gap="30px">
                <Grid item md={8} sm={12}>
                  <NewFriendForm
                    values={values}
                    handleChange={handleChange}
                    errors={errors}
                    setFieldValue={setFieldValue}
                  />
                </Grid>
                <Grid item md={4} sm={12}>
                  <OrganizationStructure
                    values={values}
                    handleChange={handleChange}
                    errors={errors}
                    handleSubmit={handleSubmit}
                    setFieldValue={setFieldValue}
                    isLoading={isLoading}
                    showZoneLocator={false}
                    showBlock
                    disabledOSKeys={OS_KEYS}
                  />
                </Grid>
              </Box>
              <Divider
                sx={{
                  display: "flex",
                  width: "100%",
                  paddingTop: "1%",
                  marginBottom: "30px",
                }}
              />
              <div style={{ display: "flex", gap: 5 }}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  style={CommonStyles.buttonStyle}
                  label="Submit"
                  type="submit"
                />
                <Button
                  variant="outlined"
                  onClick={onBack}
                  label="Cancel"
                  style={{
                    ...CommonStyles.buttonStyle,
                    marginLeft: "20px",
                  }}
                />
              </div>
              <Alert
                open={openAlert}
                onClose={toggleAlert}
                onSubmit={() => handleAlertYes(values)}
              />
            </>
          )}
        </Formik>
      </Grid>
      <Toast
        open={!!showToast}
        severity={showToast?.severity || ""}
        message={showToast?.message || ""}
        handleClose={() => {
          setShowToast(null);
        }}
      />
    </>
  );
}

export default MemberCreate;
